import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectShipLoose } from "../../../redux/selectors";
import { postData, deleteData } from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const ShipLoose = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "shipLoose";

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectShipLoose);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails || {};
  }, [storedJobDetails, activetab]);
  
  const loading = storedJobDetails.length === 0;
    const [error, setError] = useState(null);

  const { formData, setFormData } = useFormState([]);

  useEffect(() => {
    const filteredItems = storedJobDetails.filter(
      (item) => item.Zone === activetab && item.ProjectID === jobDetails.ProjectID
    );
  
    setFormData(filteredItems);
  }, [storedJobDetails, activetab, jobDetails, setFormData]);


  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  
  // Get user roles to check for Scheduling permission
  const mappedUserRoles = useSelector(state => state.user.rolesMapped);
  
  // Allow users with Scheduling role to edit data
  const hasSchedulingRole = mappedUserRoles?.Scheduling === true;
  
  // Update the isDisabled condition to allow Scheduling role to edit
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut && !hasSchedulingRole) || readOnly;

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
    Scheduling: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const isFieldTbd = (fieldValue) => {
    if (typeof fieldValue === "string") {
      fieldValue = fieldValue.trim().toLowerCase();
      return fieldValue.includes("tbd");
    }
    return false;
  };

  const evaluateZoneTbdFields = (item) => {
    if (!item) return {};
  
    return Object.keys(item).reduce((acc, key) => {
      acc[key] = isFieldTbd(item[key]);
      return acc;
    }, {});
  };
  

  const handleAddItem = async () => {
    const newItem = {
      projectID: jobId,
      zone: activetab,
      saleQuoteID: jobDetails.SaleQuoteID || "",
      hasShipLoose: "true",
    };
  
    // Validate newItem object
    if (!newItem.projectID || !newItem.zone || !newItem.hasShipLoose) {
      setError("All required fields must be filled out before adding an item.");
      return;
    }
  
    try {
      // Post the new item
      await dispatch(postData({ url: apiUrl, data: newItem })).unwrap();
  
      // Post to change log after successful addition
      await dispatch(postChangeLog({
        projectID: jobId,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Ship Loose",
        field: "Ship Loose Item",
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();
  
      // Refetch job details
      await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
  
      // Update form data
      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
      setFormData((prevData) => {
        const updatedFormData = [...prevData];
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((item) => {
            if (item.Zone === activetab && item.ProjectID === jobDetails.ProjectID) {
              updatedFormData.push(item);
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to add item:", error);
      setError(error.message || "An error occurred while adding the item.");
    }
  };
  
  const handleRemoveItem = async (index) => {
    const itemId = formData[index]?.ID;
    const itemToDelete = formData[index];
    
    if (!itemId) {
      setError("Invalid item ID.");
      return;
    }
  
    try {
      // Delete the specified item and refetch job details
      await dispatch(deleteData({ url: apiUrl, data: { ID: itemId } })).unwrap();

      // Only log if the item has meaningful data (not just added and immediately removed)
      if (itemToDelete && (itemToDelete.Title || itemToDelete.Description || itemToDelete.Quantity)) {
        // Post to change log after successful deletion
        await dispatch(postChangeLog({
          projectID: jobId,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Ship Loose",
          field: "Ship Loose Item",
          prevValue: "",
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      }
  
      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();
  
      setFormData((prevData) => {
        const updatedFormData = prevData.filter((_, i) => i !== index);
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((item) => {
            if (item.Zone === activetab && item.ProjectID === jobDetails.ProjectID) {
              updatedFormData.push(item);
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to delete item:", error);
      setError(error.message || "An error occurred while deleting the item.");
    }
  };

  const handleChangeItem = (e, index) => {
    const { name, value } = e.target;
    setFormData((prevData) =>
      prevData.map((item, i) =>
        i === index ? { ...item, [name]: value } : item
      )
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : loading ? (
        <p>Loading...</p>
      ) : (
        <div className="ship-loose-responsive">
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          {formData.map((data, index) => {
          const zoneTbdFields = evaluateZoneTbdFields(data);
          return (
            <Row key={data.ID} className="mb-3 ship-loose-record" style={{ border: "1px solid #ccc", padding: "10px", borderRadius: "5px" }}>
              <Col className="ship-loose-record-content">
                <Row className="ship-loose-primary-fields">
                  {/* Title */}
                  <Col>
                    <Form.Group as={Row} controlId={`Title-${data.ID}`}>
                      <Form.Label column md={3}>
                        Title
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="Title"
                          value={data.Title || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Title", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Title",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Scheduling)}
                          className={zoneTbdFields?.Title ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Title"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  {/* Quantity */}
                  <Col md={3}>
                    <Form.Group as={Row} controlId={`Quantity-${data.ID}`}>
                      <Form.Label column md={3}>
                        Quantity
                      </Form.Label>
                      <Col sm={9}>
                        <Form.Control
                          type="text"
                          name="Quantity"
                          value={data.Quantity || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Quantity", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Quantity",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Scheduling)}
                          className={zoneTbdFields?.Quantity ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Quantity"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mt-3 ship-loose-description-row">
                  {/* Description */}
                  <Col>
                    <Form.Group as={Row} controlId={`Description-${data.ID}`}>
                      <Form.Label>
                        Description
                      </Form.Label>
                      <Col>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="Description"
                          value={data.Description || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Description", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Description",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Scheduling)}
                          className={zoneTbdFields?.Description ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Description"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              {/* Delete Button */}
              <Col sm={1} className="ship-loose-actions">
                <Button
                  variant="link"
                  onClick={() => handleRemoveItem(index)}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Scheduling)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </Col>
            </Row>
          );
        })}
          <Button
            className="responsive-add-row-button"
            variant="primary"
            onClick={handleAddItem}
            disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Scheduling)}
          >
            Add Item
          </Button>

        </div>
      )}
    </div>
  );
};

export default ShipLoose;
